import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { cn } from '@/shared/utils';
import { MetadataFieldList } from './metadata-field-list';
import { MetadataGroupSidebar } from './metadata-group-sidebar';
import type {
  CustomerAccess,
  MetadataBuilderProps,
  MetadataBuilderState,
  MetadataField,
  MetadataGroup,
} from './types';
import {
  builderStateToJsonSchema,
  createNewGroup,
  getGroupsWithErrors,
  jsonSchemaToBuilderState,
} from './utils';

export interface MetadataBuilderHeader {
  title: string;
  description?: string;
}

export function MetadataBuilder({
  value,
  onChange,
  disabled = false,
  className,
  allowedCustomerAccess = ['none', 'read', 'read-and-write'],
  fieldErrors = [],
  header,
}: MetadataBuilderProps & { header?: MetadataBuilderHeader | false }) {
  const { t } = useIntl();

  // Internal state
  const [state, setState] = useState<MetadataBuilderState>(() => jsonSchemaToBuilderState(value));
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    () => state.groups[0]?.id || null
  );

  // Compute groups with errors
  const groupsWithErrors = useMemo(
    () => getGroupsWithErrors(state.fields, fieldErrors),
    [state.fields, fieldErrors]
  );

  // Sync from external value when it changes
  useEffect(() => {
    const newState = jsonSchemaToBuilderState(value);
    setState(newState);
    // Keep selected group if it still exists, otherwise select first
    setSelectedGroupId(
      (prev) => newState.groups.find((g) => g.id === prev)?.id || newState.groups[0]?.id || null
    );
  }, [value]);

  // Emit changes to parent
  const emitChanges = useCallback(
    (newState: MetadataBuilderState) => {
      if (onChange) {
        const schema = builderStateToJsonSchema(newState);
        onChange(schema);
      }
    },
    [onChange]
  );

  // Group handlers
  const handleAddGroup = useCallback(
    (labelDe: string, labelEn?: string) => {
      const newGroup = createNewGroup(state.groups);
      newGroup.labelDe = labelDe;
      newGroup.labelEn = labelEn;
      const newState = {
        ...state,
        groups: [...state.groups, newGroup],
      };
      setState(newState);
      setSelectedGroupId(newGroup.id);
      emitChanges(newState);
    },
    [state, emitChanges]
  );

  const handleReorderGroups = useCallback(
    (groups: MetadataGroup[]) => {
      const newState = { ...state, groups };
      setState(newState);
      emitChanges(newState);
    },
    [state, emitChanges]
  );

  const handleEditGroup = useCallback(
    (group: MetadataGroup) => {
      const newState = {
        ...state,
        groups: state.groups.map((g) => (g.id === group.id ? group : g)),
      };
      setState(newState);
      emitChanges(newState);
    },
    [state, emitChanges]
  );

  const handleDeleteGroup = useCallback(
    (groupId: string) => {
      // Move fields from deleted group to first remaining group
      const remainingGroups = state.groups.filter((g) => g.id !== groupId);
      const fallbackGroupId = remainingGroups[0]?.id;

      const newFields = fallbackGroupId
        ? state.fields.map((f) => (f.groupId === groupId ? { ...f, groupId: fallbackGroupId } : f))
        : state.fields.filter((f) => f.groupId !== groupId);

      const newState = {
        groups: remainingGroups.length > 0 ? remainingGroups : [createNewGroup([])],
        fields: newFields,
      };

      setState(newState);
      setSelectedGroupId(newState.groups[0]?.id || null);
      emitChanges(newState);
    },
    [state, emitChanges]
  );

  // Field handlers
  const handleFieldsChange = useCallback(
    (fields: MetadataField[]) => {
      const newState = { ...state, fields };
      setState(newState);
      emitChanges(newState);
    },
    [state, emitChanges]
  );

  // Computed values
  const selectedGroup = useMemo(
    () => state.groups.find((g) => g.id === selectedGroupId) || null,
    [state.groups, selectedGroupId]
  );

  const fieldsPerGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const field of state.fields) {
      counts[field.groupId] = (counts[field.groupId] || 0) + 1;
    }
    return counts;
  }, [state.fields]);

  // Determine header content
  const showHeader = header !== false;
  const headerTitle = header ? header.title : t('metadata.builder.title');
  const headerDescription = header ? header.description : t('metadata.builder.description');

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-card rounded-lg border shadow-sm overflow-hidden',
        className
      )}
    >
      {/* Header - optional */}
      {showHeader && (
        <div className="border-b p-5">
          <h2 className="text-base font-semibold">{headerTitle}</h2>
          {headerDescription && (
            <p className="text-sm text-muted-foreground">{headerDescription}</p>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 min-h-0">
        {/* Groups Sidebar */}
        <MetadataGroupSidebar
          groups={state.groups}
          selectedGroupId={selectedGroupId}
          onSelectGroup={setSelectedGroupId}
          onReorderGroups={handleReorderGroups}
          onAddGroup={handleAddGroup}
          onEditGroup={handleEditGroup}
          onDeleteGroup={handleDeleteGroup}
          disabled={disabled}
          fieldsPerGroup={fieldsPerGroup}
          groupsWithErrors={groupsWithErrors}
        />

        {/* Fields Area */}
        <MetadataFieldList
          fields={state.fields}
          groups={state.groups}
          selectedGroup={selectedGroup}
          onSelectGroup={setSelectedGroupId}
          disabled={disabled}
          allowedCustomerAccess={allowedCustomerAccess as CustomerAccess[]}
          onFieldsChange={handleFieldsChange}
          fieldErrors={fieldErrors}
        />
      </div>
    </div>
  );
}

export { MetadataFormPreview } from './metadata-form-preview';
// Re-export types and utils for consumers
export * from './types';
export {
  builderStateToJsonSchema,
  createDefaultGroup,
  createNewField,
  createNewGroup,
  getGroupsWithErrors,
  jsonSchemaToBuilderState,
  parseMetadataApiErrors,
  reorderArray,
  updateOrderValues,
} from './utils';
